import React, { memo, useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useAppServices } from "hook/services";

const CURRENCIES = [
  { label: "£ GBP", value: "gbp" },
  { label: "$ USD", value: "usd" },
];

const labelFor = (value) => CURRENCIES.find((c) => c.value === value)?.label || "";

const EditAITokenPlanModal = memo(({ open, onClose, plan, onSaved }) => {
  const AppService = useAppServices();
  const [tokens, setTokens] = useState(plan?.tokens?.toString() ?? "");
  const [currency, setCurrency] = useState(plan?.currency ?? "gbp");
  const [amount, setAmount] = useState(plan?.amount?.toString() ?? '');
  const [planName, setPlanName] = useState(plan?.planName ?? "");
  const [saving, setSaving] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (plan) {
      setTokens(plan?.tokens ?? "");
      setCurrency(plan?.currency || "gbp");
      setAmount(plan?.amount?.toString() ?? "");
      setPlanName(plan?.planName ?? "");
    }
  }, [plan]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  if (!open || !plan) return null;

  const close = () => {
    if (saving) return;
    setDropdownOpen(false);
    onClose();
  };

  const save = async () => {
    if (!tokens || !amount) return;
    setSaving(true);
    const { response } = await AppService.aiTokenPlans.update({
      payload: { _id: plan._id, tokens, amount, currency, planName },
      toaster: true,
    });
    setSaving(false);
    if (response?.success) {
      onSaved && onSaved(response.data);
      onClose();
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={close}
    >
      <div
        style={{
          width: 460,
          maxWidth: "92vw",
          background: "#fff",
          borderRadius: 10,
          padding: 24,
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
          <b style={{ fontSize: 18 }}>Edit AI Task  Plan</b>
          <CloseIcon style={{ cursor: "pointer", color: "#757575" }} onClick={close} />
        </div>

        <div style={{ marginTop: 18 }}>
          <label style={{ fontSize: 14, fontWeight: 600 }}>Task Plan Name</label>
          <input
            type="text"
            value={planName}
            onChange={(e) => setPlanName(e.target.value)}
            style={{
              width: "100%",
              marginTop: 6,
              padding: "10px 12px",
              border: "1px solid #1E1E1E",
              borderRadius: 6,
              fontSize: 14,
              outline: "none",
            }}
          />
        </div>

        <div style={{ marginTop: 18 }}>
          <label style={{ fontSize: 14, fontWeight: 600 }}>Number of AI Tasks</label>
          <input
            type="number"
            value={tokens}
            onChange={(e) => setTokens(e.target.value)}
            style={{
              width: "100%",
              marginTop: 6,
              padding: "10px 12px",
              border: "1px solid #1E1E1E",
              borderRadius: 6,
              fontSize: 14,
              outline: "none",
            }}
          />
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={{ fontSize: 14, fontWeight: 600 }}>Select Currency</label>
          <div ref={dropdownRef} style={{ position: "relative", marginTop: 6 }}>
            <button
              type="button"
              onClick={() => setDropdownOpen((o) => !o)}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #D9D9D9",
                borderRadius: 6,
                fontSize: 14,
                background: "#fff",
                color: "#1E1E1E",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                outline: "none",
              }}
            >
              <span>{labelFor(currency)}</span>
              <KeyboardArrowDownIcon
                style={{
                  color: "#757575",
                  transition: "transform 0.15s",
                  transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </button>

            {dropdownOpen && (
              <ul
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: "calc(100% + 6px)",
                  background: "#fff",
                  border: "1px solid #D9D9D9",
                  borderRadius: 6,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                  zIndex: 10,
                  overflow: "hidden",
                  margin: 0,
                  padding: 0,
                  listStyle: "none",
                }}
              >
                {CURRENCIES.map((c) => {
                  const isSelected = c.value === currency;
                  return (
                    <li key={c.value}>
                      <button
                        type="button"
                        onClick={() => {
                          setCurrency(c.value);
                          setDropdownOpen(false);
                        }}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "10px 12px",
                          fontSize: 14,
                          color: "#1E1E1E",
                          background: isSelected ? "#F3F4F6" : "transparent",
                          fontWeight: isSelected ? 600 : 400,
                          border: "none",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#F3F4F6")}
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = isSelected ? "#F3F4F6" : "transparent")
                        }
                      >
                        {c.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={{ fontSize: 14, fontWeight: 600 }}>Cost of AI Tasks</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={{
              width: "100%",
              marginTop: 6,
              padding: "10px 12px",
              border: "1px solid #D9D9D9",
              borderRadius: 6,
              fontSize: 14,
              outline: "none",
            }}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 22 }}>
          <button
            onClick={close}
            disabled={saving}
            style={{
              padding: "8px 20px",
              border: "1px solid #D9D9D9",
              background: "#fff",
              borderRadius: 6,
              fontSize: 14,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !tokens || !amount}
            style={{
              padding: "8px 22px",
              border: "none",
              background: "#1E1E1E",
              color: "#fff",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: saving || !tokens || !amount ? "not-allowed" : "pointer",
              opacity: saving || !tokens || !amount ? 0.6 : 1,
            }}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
})
export default EditAITokenPlanModal